import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../../../api/client';
import { getInterviewRowId } from './interviewUtils';

export const useInterviewData = () => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const filtersInitialized = useRef(false);

    // Pagination state
    const [paginationData, setPaginationData] = useState({
        page: 1,
        total_pages: 1,
        total_count: 0,
        has_next: false,
        has_previous: false,
        page_size: 12
    });

const [page, setPage] = useState(() => {
    try {

        const savedPage = Number(localStorage.getItem('interviewOverview_page'));
        const hasSavedPage = Number.isInteger(savedPage) && savedPage > 0;
        const lastRoute = sessionStorage.getItem('lastRoute') || '';

        // Preserve saved page when navigating back from candidate details or
        // when the last route is interview-overview, but NOT on full page reload.
        if (hasSavedPage && (lastRoute.startsWith('/candidatedetails') || lastRoute === '/interview-overview')) {
            return savedPage;
        }

        return 1;
    } catch (e) {
        return 1;
    }
});


const [pageSize, setPageSize] = useState(() => {
  try {
    const saved = Number(localStorage.getItem('interviewOverview_page_size'));
    return ([12, 25, 50, 100].includes(saved)) ? saved : 12;
  } catch (e) {
    return 12;
  }
});

    // Filter states
    const [filters, setFilters] = useState(() => {
        let fromUrl = {};
        try {
            const params = new URLSearchParams(window.location.search);
            fromUrl = {
                scheduled_by: params.get('scheduled_by') || '',
                company_id: params.get('company_id') || '',
                filter_date_from: params.get('filter_date_from') || '',
                filter_date_to: params.get('filter_date_to') || '',
            };
        } catch (e) {
            fromUrl = {};
        }
        return {
            search: '',
            candidate_name: '',
            company_id: '',
            department_id: '',
            job_description_id: '',
            scheduled_by: '',
            status: '',
            tags: '',
            filter_date_from: '',
            filter_date_to: '',
            show_archived: '',
            ordering: '-started_at',
            ...fromUrl,
        };
    });

    const [statusCounts, setStatusCounts] = useState(null);

    // Sort state
    const [sortConfig, setSortConfig] = useState({
        key: 'started_at',
        direction: 'desc'
    });

    // Dropdown options
    const [dropdownOptions, setDropdownOptions] = useState({
        companies: [],
        departments: [],
        jobDescriptions: [],
        companyAdmins: []
    });

    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // Fetch interview candidates
    const fetchInterviewCandidates = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const isSeeAllMode = pageSize > 100;
            const requestPage = isSeeAllMode ? '1' : page.toString();
            const requestPageSize = isSeeAllMode ? '50' : pageSize.toString();

            const params = new URLSearchParams({
                page: requestPage,
                page_size: requestPageSize
            });

            // Add filters if present
            Object.entries(filters).forEach(([key, value]) => {
                if (value && value.toString().trim()) {
                    params.append(key, value.toString().trim());
                }
            });

            const response = await api.get(`interview-candidates/?${params.toString()}`, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('adminToken')}`,
                },
            });

            const { candidates: candidateResults, pagination, status_counts } = response.data;

            setCandidates(Array.isArray(candidateResults) ? candidateResults : []);
            if (status_counts) {
                setStatusCounts(status_counts);
            }
            setPaginationData(pagination || {
                page: 1,
                total_pages: 1,
                total_count: 0,
                has_next: false,
                has_previous: false,
                page_size: isSeeAllMode ? 50 : 12
            });
        } catch (err) {
            console.error('Error fetching interview candidates:', err);
            setError('Failed to fetch interview data');
            setCandidates([]);
            setPaginationData({
                page: 1,
                total_pages: 1,
                total_count: 0,
                has_next: false,
                has_previous: false,
                page_size: 12
            });
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, filters]);

    // Fetch next batch when in "See All" mode and scrolling near bottom
    const fetchNextBatch = useCallback(async () => {
        if (loading || isLoadingMore) return;
        if (pageSize <= 100) return;
        if (candidates.length >= (paginationData?.total_count || 0)) return;

        setIsLoadingMore(true);
        try {
            const nextPage = Math.floor(candidates.length / 50) + 1;
            const params = new URLSearchParams({
                page: nextPage.toString(),
                page_size: '50'
            });

            Object.entries(filters).forEach(([key, value]) => {
                if (value && value.toString().trim()) {
                    params.append(key, value.toString().trim());
                }
            });

            const response = await api.get(`interview-candidates/?${params.toString()}`, {
                headers: {
                    'Authorization': `Token ${localStorage.getItem('adminToken')}`,
                },
            });

            const { candidates: newCandidates, pagination } = response.data;
            if (Array.isArray(newCandidates) && newCandidates.length > 0) {
                setCandidates(prev => {
                    const existingMap = new Set(prev.map(c => getInterviewRowId(c)));
                    const uniqueNew = newCandidates.filter(c => !existingMap.has(getInterviewRowId(c)));
                    return [...prev, ...uniqueNew];
                });
            }
            if (pagination) {
                setPaginationData(prev => ({
                    ...prev,
                    has_next: pagination.has_next,
                    total_count: pagination.total_count || prev.total_count
                }));
            }
        } catch (err) {
            console.error('Error fetching next batch:', err);
        } finally {
            setIsLoadingMore(false);
        }
    }, [loading, isLoadingMore, pageSize, candidates.length, paginationData?.total_count, filters]);

    // Fetch dropdown options
    const fetchDropdownOptions = async () => {
    try {
        // Fetch companies from API
        const companiesResponse = await api.get('companies/', {
            headers: {
                'Authorization': `Token ${localStorage.getItem('adminToken')}`,
            },
        });
        
        const companies = Array.isArray(companiesResponse.data) 
            ? companiesResponse.data 
            : (companiesResponse.data.results || []);

        // Fetch departments from API
        const departmentsResponse = await api.get('departments/', {
            headers: {
                'Authorization': `Token ${localStorage.getItem('adminToken')}`,
            },
        });
        
        const departments = Array.isArray(departmentsResponse.data) 
            ? departmentsResponse.data 
            : (departmentsResponse.data.results || []);

        // Fetch job descriptions from existing API
        const jdResponse = await api.get('job-descriptions/', {
            headers: {
                'Authorization': `Token ${localStorage.getItem('adminToken')}`,
            },
        });
        
        const jobDescriptions = Array.isArray(jdResponse.data) 
            ? jdResponse.data 
            : (jdResponse.data.results || []);

        const adminLists = await Promise.all(
            companies.map(async (company) => {
                if (!company?.id) return [];
                try {
                    const response = await api.get(`companies/${company.id}/users/`, {
                        params: { role: 'admin', page_size: 200 },
                        headers: {
                            'Authorization': `Token ${localStorage.getItem('adminToken')}`,
                        },
                    });
                    return Array.isArray(response.data)
                        ? response.data
                        : (response.data.results || []);
                } catch (err) {
                    return [];
                }
            })
        );

        const adminsById = new Map();
        adminLists.flat().forEach((row) => {
            const user = row?.user || row;
            if (!user?.id || adminsById.has(user.id)) return;
            const name = [user.firstName || user.first_name, user.lastName || user.last_name]
                .filter(Boolean)
                .join(' ')
                .trim() || user.email || '';
            if (name) adminsById.set(user.id, { id: user.id, name });
        });
        const companyAdmins = Array.from(adminsById.values())
            .sort((a, b) => a.name.localeCompare(b.name));

        setDropdownOptions({
            companies,
            departments,
            jobDescriptions,
            companyAdmins
        });

    } catch (err) {
        console.error('Error fetching dropdown options:', err);
        setError('Failed to fetch filter options');
        
        // Set empty arrays as fallback
        setDropdownOptions({
            companies: [],
            departments: [],
            jobDescriptions: [],
            companyAdmins: []
        });
    }
};
    // Handle filter changes
    const updateFilter = (filterName, value) => {
        if (typeof filterName === 'object' && filterName !== null) {
            setFilters(prev => ({
                ...prev,
                ...filterName
            }));
            return;
        }
        setFilters(prev => ({
            ...prev,
            [filterName]: value
        }));
    };

    // Clear all filters
    const clearAllFilters = () => {
        setFilters({
            search: '',
            candidate_name: '',
            company_id: '',
            department_id: '',
            job_description_id: '',
            scheduled_by: '',
            status: '',
            tags: '',
            filter_date_from: '',
            filter_date_to: '',
            show_archived: '',
            ordering: '-started_at'
        });
    };

    // Check if any filters are active
    const hasActiveFilters = () => {
        return !!(
            filters.search || filters.candidate_name || filters.company_id || filters.department_id ||
            filters.job_description_id || filters.scheduled_by || filters.status || filters.tags ||
            filters.filter_date_from || filters.filter_date_to || filters.show_archived
        );
    };

 // updatePage persists to localStorage
const updatePage = (newPage) => {
  setPage(newPage);
  try {
    localStorage.setItem('interviewOverview_page', String(newPage));
  } catch (e) { /* ignore */ }
};


// updatePageSize persists standard sizes (12, 25, 50, 100) and resets page to 1
const updatePageSize = (newPageSize) => {
  const numericSize = Number(newPageSize);
  setPageSize(numericSize);
  setPage(1);
  try {
    // Only standard options are persisted in localStorage; "See All" is not saved
    if ([12, 25, 50, 100].includes(numericSize)) {
      localStorage.setItem('interviewOverview_page_size', String(numericSize));
    } else {
      localStorage.removeItem('interviewOverview_page_size');
    }
    localStorage.setItem('interviewOverview_page', '1'); // reflect page reset
  } catch (e) { /* ignore */ }
};

    // Effect for handling filter changes with debouncing
    // Use refs for the latest fetch function and current page so this effect
    // only depends on `filters` and doesn't re-run when `page` or callback
    // identity changes (avoids a dependency cycle when navigating back).
    const fetchRef = useRef(fetchInterviewCandidates);
    const pageRef = useRef(page);

    // Keep refs up-to-date
    useEffect(() => { fetchRef.current = fetchInterviewCandidates; }, [fetchInterviewCandidates]);
    useEffect(() => { pageRef.current = page; }, [page]);

    useEffect(() => {
      const timer = setTimeout(() => {
          // Skip the very first run (mount). We only want to reset to page 1
          // when filters change due to user interaction, not on initialization.
          if (!filtersInitialized.current) {
              filtersInitialized.current = true;
              return; // do nothing on initial mount
          }

          if (pageRef.current !== 1) {
              setPage(1); // will trigger fetch via the page effect
          } else {
              fetchRef.current(); // already on page 1: fetch now
          }
      }, 300); // 300ms debounce

      return () => clearTimeout(timer);
  }, [filters]);

    // Sort functions
    const handleSort = (key) => {
        let direction = 'asc';
        
        // If clicking the same column, toggle direction
        if (sortConfig.key === key) {
            direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
        }
        
        // Update sort config
        setSortConfig({ key, direction });
        
        // Update filters with new ordering
        const ordering = direction === 'desc' ? `-${key}` : key;
        updateFilter('ordering', ordering);
        
        // Reset to first page when sorting
        setPage(1);
    };

    // Get sort direction for a column
    const getSortDirection = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction;
        }
        return null;
    };

    // Effect for fetching data when page or pageSize changes
useEffect(() => {
  fetchDropdownOptions();
}, []);

// Fetch candidates when pagination changes
useEffect(() => {
    fetchInterviewCandidates();
}, [page, pageSize]);

    return {
        // Data
        candidates,
        loading,
        error,
        paginationData,
        dropdownOptions,
        
        // Filter state
        filters,
        statusCounts,
        
        // Filter actions
        updateFilter,
        clearAllFilters,
        hasActiveFilters,
        
        // Pagination actions
        page,
        pageSize,
        updatePage,
        updatePageSize,
        
        // Sorting actions
        handleSort,
        getSortDirection,
        sortConfig,
        
        // See All & Infinite Virtualization
        isSeeAll: pageSize > 100,
        hasMore: (pageSize > 100) && (candidates.length < (paginationData?.total_count || 0)),
        isLoadingMore,
        fetchNextBatch,

        // Utility actions
        refetch: fetchInterviewCandidates,
        refetchOptions: fetchDropdownOptions
    };
};

export default useInterviewData;